import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MessageSquareHeart, X, Send, Star } from "lucide-react";


export function BetaFeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="beta-feedback-btn"
        onClick={()=>setOpen(true)}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full
          bg-[var(--accent)] text-black font-mono text-xs uppercase tracking-widest shadow-lg
          hover:scale-105 transition-transform">
        <MessageSquareHeart size={15}/>
        <span className="hidden sm:inline">Beta feedback</span>
      </button>
      {open && <FeedbackModal onClose={()=>setOpen(false)}/>}
    </>
  );
}


function FeedbackModal({ onClose }) {
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("feature");
  const [rating, setRating] = useState(0);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (message.trim().length < 5) { toast.error("Please tell us a bit more (5+ characters)"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        category,
        rating: rating || undefined,
        email: email || undefined,
        url: window.location.pathname + window.location.search,
        browser_info: navigator.userAgent,
      });
      toast.success("Thanks — we read every message!");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send feedback");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      data-testid="feedback-modal" onClick={onClose}>
      <div className="card-sharp p-6 max-w-lg w-full sm:rounded-lg" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <MessageSquareHeart size={22} className="text-[var(--accent)]"/>
          <div className="flex-1">
            <h3 className="font-display text-xl text-white">Send beta feedback</h3>
            <p className="text-[11px] font-mono text-[var(--text-muted)]">
              Bug? Idea? Something confusing? We read every message.
            </p>
          </div>
          <button data-testid="feedback-close" onClick={onClose}
            className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]"><X size={14}/></button>
        </div>

        <label className="label-tiny">Category</label>
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="feedback-category">
          {[
            {v: "bug", l: "🐛 Bug"},
            {v: "feature", l: "💡 Feature"},
            {v: "pricing", l: "💰 Pricing"},
            {v: "general", l: "💬 General"},
          ].map(o => (
            <button key={o.v}
              data-testid={`feedback-cat-${o.v}`}
              onClick={()=>setCategory(o.v)}
              className={`chip cursor-pointer ${category === o.v ? "!border-[var(--accent)] !text-[var(--accent)]" : ""}`}>
              {o.l}
            </button>
          ))}
        </div>

        <label className="label-tiny">Your feedback</label>
        <textarea data-testid="feedback-message" rows={5}
          value={message} onChange={(e)=>setMessage(e.target.value)}
          placeholder="What's on your mind? The more specific, the better."
          className="input-dark !font-sans"/>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="label-tiny">Email (optional)</label>
            <input data-testid="feedback-email"
              type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
              placeholder="so we can reply"
              className="input-dark !text-xs"/>
          </div>
          <div>
            <label className="label-tiny">Overall rating (optional)</label>
            <div className="flex gap-1 pt-1" data-testid="feedback-rating">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={()=>setRating(rating === n ? 0 : n)}
                  data-testid={`feedback-star-${n}`}
                  className={`p-1 hover:scale-110 transition-transform ${n <= rating ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}>
                  <Star size={16} fill={n <= rating ? "currentColor" : "none"}/>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button data-testid="feedback-submit" onClick={submit} disabled={busy}
          className="btn-primary w-full mt-5 flex items-center justify-center gap-2">
          <Send size={13}/> {busy ? "Sending…" : "Send feedback"}
        </button>
      </div>
    </div>
  );
}
